// src/utils/searchUtils.ts - Utilitaires pour la recherche insensible à la casse et aux accents

/**
 * Normalise une chaîne de caractères pour la recherche :
 * - Convertit en minuscules
 * - Supprime les accents/diacritiques
 * 
 * @param str - La chaîne à normaliser (peut être undefined ou null)
 * @returns La chaîne normalisée (chaîne vide si str est falsy)
 * 
 * @example
 * normalizeSearch("Créme") // "creme"
 * normalizeSearch("ÉLÉGANCE") // "elegance"
 * normalizeSearch(undefined) // ""
 */
export const normalizeSearch = (str: string | undefined | null): string => {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

/**
 * Vérifie si une chaîne contient un terme de recherche (insensible à la casse et aux accents)
 * 
 * @param text - Le texte dans lequel chercher
 * @param searchTerm - Le terme à rechercher
 * @returns true si le texte contient le terme de recherche
 * 
 * @example
 * matchesSearch("Crème hydratante", "creme") // true
 * matchesSearch("Élégance", "elegance") // true
 */
export const matchesSearch = (text: string | undefined | null, searchTerm: string): boolean => {
  // Si pas de terme de recherche, on accepte tout
  if (!searchTerm) return true;

  const normalizedText = normalizeSearch(text);
  const normalizedSearch = normalizeSearch(searchTerm);

  // Découper la recherche en "blocs" (mots), ex: "tv his" -> ["tv", "his"]
  const tokens = normalizedSearch.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) {
    return true;
  }

  // Tous les blocs doivent être présents quelque part dans le texte
  // Exemple : "tv his" doit matcher "tv 32 hisense smart"
  return tokens.every(token => normalizedText.includes(token));
};

/**
 * Vérifie si un terme de recherche correspond à au moins un des champs fournis.
 * Chaque bloc de la recherche doit apparaître dans au moins un champ.
 *
 * Exemple :
 * - searchTerm = "tv his"
 * - fields = [name, brand, category]
 * => "tv" peut être dans le name et "his" dans la brand.
 */
export const matchesSearchInFields = (
  fields: Array<string | undefined | null>,
  searchTerm: string
): boolean => {
  if (!searchTerm) return true;

  const normalizedSearch = normalizeSearch(searchTerm);
  const tokens = normalizedSearch.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) return true;

  const normalizedFields = fields.map(field => normalizeSearch(field));

  // Chaque token doit apparaître dans au moins un des champs
  return tokens.every(token =>
    normalizedFields.some(field => field.includes(token))
  );
};
